import React, { useCallback, useEffect, useState } from 'react';
import './LocalBuildsImport.css';
import { useGeneratorContext } from '../../contexts/GeneratorStateContext';
import { NOTIFICATION, useNotification } from '../NotificationSystem';
import * as buildStorage from '../../services/buildStorage';

const TXT_TITLE = {
    fr: 'Stuffs enregistrés sur cet appareil',
    en: 'Builds saved on this device',
    de: 'Auf diesem Gerät gespeicherte Builds',
    es: 'Builds guardadas en este dispositivo',
    pt: 'Builds guardadas neste dispositivo',
};
const TXT_SUBTITLE = {
    fr: 'Ces stuffs ont été créés sans être connecté et vivent dans ce navigateur. Les importer les copie sur votre compte ; la copie locale reste en place, et un stuff déjà importé est ignoré.',
    en: 'These builds were made while signed out and live in this browser. Importing copies them onto your account; the local copy stays where it is, and a build already imported is skipped.',
    de: 'Diese Builds wurden ohne Anmeldung erstellt und liegen in diesem Browser. Beim Import werden sie in dein Konto kopiert; die lokale Kopie bleibt bestehen, und ein bereits importierter Build wird übersprungen.',
    es: 'Estas builds se crearon sin haber iniciado sesión y viven en este navegador. Importarlas las copia en tu cuenta; la copia local se mantiene y una build ya importada se omite.',
    pt: 'Estas builds foram criadas sem sessão iniciada e vivem neste navegador. Importá-las copia-as para a sua conta; a cópia local mantém-se e uma build já importada é ignorada.',
};
const TXT_COUNT = {
    fr: n => `${n} stuff${n > 1 ? 's' : ''} sur cet appareil.`,
    en: n => `${n} build${n > 1 ? 's' : ''} on this device.`,
    de: n => `${n} Build${n > 1 ? 's' : ''} auf diesem Gerät.`,
    es: n => `${n} build${n > 1 ? 's' : ''} en este dispositivo.`,
    pt: n => `${n} build${n > 1 ? 's' : ''} neste dispositivo.`,
};
const TXT_IMPORT = {
    fr: 'Importer sur mon compte',
    en: 'Import into my account',
    de: 'In mein Konto importieren',
    es: 'Importar a mi cuenta',
    pt: 'Importar para a minha conta',
};
const TXT_IMPORTING = {
    fr: 'Import en cours...',
    en: 'Importing...',
    de: 'Wird importiert...',
    es: 'Importando...',
    pt: 'A importar...',
};
const MSG_IMPORTED = {
    fr: n => `${n} stuff${n > 1 ? 's' : ''} importé${n > 1 ? 's' : ''}.`,
    en: n => `${n} build${n > 1 ? 's' : ''} imported.`,
    de: n => `${n} Build${n > 1 ? 's' : ''} importiert.`,
    es: n => `${n} build${n > 1 ? 's' : ''} importada${n > 1 ? 's' : ''}.`,
    pt: n => `${n} build${n > 1 ? 's' : ''} importada${n > 1 ? 's' : ''}.`,
};
const MSG_ALREADY = {
    fr: 'Ces stuffs sont déjà sur votre compte.',
    en: 'Those builds are already on your account.',
    de: 'Diese Builds sind bereits in deinem Konto.',
    es: 'Esas builds ya están en tu cuenta.',
    pt: 'Essas builds já estão na sua conta.',
};
const MSG_FAILED = {
    fr: "L'import a échoué.",
    en: 'The import failed.',
    de: 'Der Import ist fehlgeschlagen.',
    es: 'La importación ha fallado.',
    pt: 'A importação falhou.',
};

/**
 * Adopting the builds made before signing in. The generator offers this once,
 * as a confirm on the first bootstrap after login — answer it either way and it
 * never comes back, which left anyone who said no, or who kept building as a
 * guest afterwards, with no way to ask for it. This is that way.
 */
const LocalBuildsImport = ({ lang = 'fr' }) => {
    const { runLocalBuildImport } = useGeneratorContext();
    const { showNotification } = useNotification();
    const [localCount, setLocalCount] = useState(() => buildStorage.getBuilds().length);
    const [importing, setImporting] = useState(false);

    // The count is read once on mount and after an import: while this page is
    // open nothing else writes local builds, because the generator is elsewhere.
    useEffect(() => {
        setLocalCount(buildStorage.getBuilds().length);
    }, []);

    const onImport = useCallback(async () => {
        setImporting(true);
        const result = await runLocalBuildImport();
        setImporting(false);
        setLocalCount(buildStorage.getBuilds().length);
        if (!result) {
            showNotification(MSG_FAILED[lang] || MSG_FAILED.en, NOTIFICATION.ERROR);
            return;
        }
        if (result.imported > 0) {
            const msg = MSG_IMPORTED[lang] || MSG_IMPORTED.en;
            showNotification(msg(result.imported), NOTIFICATION.SUCCESS);
            return;
        }
        showNotification(MSG_ALREADY[lang] || MSG_ALREADY.en, NOTIFICATION.INFO);
    }, [lang, runLocalBuildImport, showNotification]);

    if (!localCount) return null;

    const count = TXT_COUNT[lang] || TXT_COUNT.en;

    return (
        <section className="local-import column left">
            <div className="local-import-heading column left">
                <h2 className="local-import-title">{TXT_TITLE[lang] || TXT_TITLE.en}</h2>
                <p className="local-import-subtitle">{TXT_SUBTITLE[lang] || TXT_SUBTITLE.en}</p>
                <p className="local-import-subtitle">{count(localCount)}</p>
            </div>
            <button
                type="button"
                className="local-import-action"
                onClick={onImport}
                disabled={importing}
            >
                {importing
                    ? (TXT_IMPORTING[lang] || TXT_IMPORTING.en)
                    : (TXT_IMPORT[lang] || TXT_IMPORT.en)}
            </button>
        </section>
    );
};

export default LocalBuildsImport;
